import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ControlLabel, Form, FormControl, FormGroup } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import qBankApi from "../services/qbankApi";

import "./Landing.css";
import testReviewImage from "./test_review.png";

type LoginBoxProps = { message?: string; }
const LoginBox = ({ message }: LoginBoxProps) => {
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [_errorMsg, setErrorMsg] = React.useState("");
    // Prefer a local error message to one that comes from the props (e.g. "you have been logged out")
    const errorMsg = _errorMsg || message;
    const history = useHistory();

    function handleLogin() {
        qBankApi.doLogin(email, password)
            .then(response => {
                if (response.status === 401) {
                    setErrorMsg("Username or password incorrect, please try again");
                } else {
                    window.location.reload();
                }
            }).catch(error => {
                console.log("Error logging in\n", error);
                setErrorMsg("Failed to connect to server, please try again");
            });
    }

    return (
        <div className="lp-login-box">
            <h2>Sign In</h2>
            <Form onSubmit={(e) => {e.preventDefault(); handleLogin()}}>
                <div className="lp-email">
                    <FormGroup controlId="lp-login-username">
                        <ControlLabel>Email</ControlLabel>
                        <FormControl
                            type="email"
                            autoComplete="username"
                            autoFocus
                            label="Email"
                            onChange={e => setEmail((e.target as any).value)}
                        />
                    </FormGroup>
                </div>
                <div className="lp-password">
                    <FormGroup controlId="lp-login-password">
                        <ControlLabel>Password</ControlLabel>
                        <FormControl
                            type="password"
                            autoComplete="current-password"
                            label="Password"
                            onChange={e => setPassword((e.target as any).value)}
                        />
                    </FormGroup>
                </div>
                <div className="lp-login-buttons">
                    <Button className="lp-forgot-password" bsStyle="link" onClick={() => history.push("/resetpassword")}>Forgot Password?</Button>&nbsp;<Button bsClass="lp-login-link" type="submit">Sign&nbsp;in</Button>
                </div>
                <div className="lp-login-error">
                    { errorMsg ? errorMsg : "\u00a0" }
                </div>
            </Form>
        </div>
    );
}

export type LandingProps = { message?: string }
const Landing = ({ message }: LandingProps) => {
    const history = useHistory();
    return (
        <>
            <header className="lp-main-header">
                <div className="header">
                    <div className="lp-container">
                        <h1 className="lp-qbank-com">
                            QBank.com
                        </h1>
                        <nav className="lp-navigation">
                            <ul className="navigation-list">
                                <li>
                                    <span className="lp-nav-item qbanks">
                                        QBanks
                                    </span>
                                </li>
                                <li>
                                    <span className="lp-nav-item">
                                        Educators
                                    </span>
                                </li>
                                <li>
                                    <span className="lp-nav-item">
                                        Pricing
                                    </span>
                                </li>
                                <li>
                                    <span className="lp-nav-item">
                                        About Us
                                    </span>
                                </li>
                            </ul>
                            <Button className="lp-sign-up" bsStyle="link" onClick={() => history.push("/register")}>Register</Button>
                        </nav>
                    </div>
                </div>
            </header>
            <div className="lp-bg-image-container">
                <div className="lp-image-overlay">
                    <div className="lp-bg-text">
                        <p className="lp-big-text">
                            Learning made simple.
                        </p>
                        <p className="lp-small-text">
                            QBank.com is an all-in-one question<br />
                            bank platform that is designed for students,<br />
                            educators, and institutions.
                        </p>
                    </div>
                    <LoginBox message={message} />
                </div>
            </div>
            <div className="lp-content">
                <h2>Key Features</h2>
                <div className="lp-features-table">
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon lp-icon-fix-cloud" icon="cloud" />Scalable, cloud-based platform</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon" icon="chart-bar" />Enhanced analytics</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon" icon="coffee" />Fast and easy set up</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon" icon="history" />Question versioning</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon" icon="desktop" />Sleek user interface</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon" icon="cog" />Customizable settings</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon lp-icon-fix-shield" icon="shield-alt" />Secure content</div>
                    <div className="lp-features-cell"><FontAwesomeIcon className="lp-icon lp-icon-fix-dollar" icon="dollar-sign" />Affordable</div>
                </div>
                <div className="lp-content-dark">
                    <h2>How it works</h2>
                    <div className="lp-works-table">
                        <div className="lp-works-cell">
                            <h3>Students</h3>
                            <p>
                                Prepare for an exam with updated, high quality practice test questions.
                                Sign up for a question bank or enter an institution specific code to
                                get started.
                            </p>
                            <div className="lp-works-cell-buttons">
                                <Button bsClass="lp-button-blue">Register</Button><Button bsClass="lp-button-gray">View&nbsp;QBanks</Button>
                            </div>
                        </div>
                        <div className="lp-works-cell">
                            <h3>Educators and Institutions</h3>
                            <p>
                                Create and publish a question bank at unprecedented speed on our secure,
                                cloud-based platform. Our UI and APIs make it easy to update your content,
                                lead your educators, and manage your user subscriptions.
                            </p>
                            <div className="lp-works-cell-buttons">
                                <Button bsClass="lp-button-blue">See&nbsp;Demo</Button><Button bsClass="lp-button-gray">Contact&nbsp;Us</Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="lp-email-section">
                    <h2>Medical School QBank</h2>
                    <div className="lp-email-table">
                        <div className="lp-email-cell">
                            <p>
                                A free, comprehensive medical student clerkship question bank is coming
                                August 1, 2020. Join the mailing list to be the first to know when
                                QBank.com goes live.
                            </p>
                            <FormGroup>
                                <FormControl
                                    type="email"
                                    label="Email"
                                    bsClass="lp-email-box"
                                    placeholder="student@example.com"
                                />
                                <Button bsClass="lp-button-gray">Request&nbsp;Invite</Button>
                            </FormGroup>
                        </div>
                        <div className="lp-email-cell">
                            <div className="lp-image-shadow">
                                <img src={testReviewImage} alt="Example of student's test results"/>
                            </div>
                        </div>
                    </div>
                </div>
                <br className="lp-clear-hack" />
            </div>
            <hr />
            <div className="lp-copyright">QBank LLC, 2020.</div>
        </>
    );
};

export default Landing;
